import { type ReactNode, useState } from 'react';
import { Editor } from '@tiptap/core';
import type { ResolvedPos } from '@tiptap/pm/model';
import { makeStyles, Menu, MenuList, MenuPopover, MenuTrigger, mergeClasses, tokens } from '@fluentui/react-components';
import { ChevronDown12Regular, Checkmark16Regular } from '@fluentui/react-icons';

import type { TIcon } from '@shared/types';
import type { ETiptapSelectorKey } from '../../../../types/tiptap.types';
import ToolbarSplitButton from '../ToolbarSplitButton';
import ToolbarToggleButton from '../ToolbarToggleButton';
import ToolbarMenuButton from '../ToolbarMenuButton';
import ToolbarMenuItem from '../ToolbarMenuItem';
import useEditorActive from '../../../../hooks/useEditorActive.hook';
import { getRootList } from '../../../../tiptap/extensions/listUtils';

const PREVIEW_INDENT = '14px';
const PREVIEW_LINE_WIDTH = '28px';

const useStyles = makeStyles({
  menuPopover: {
    minWidth: 'unset',
    width: 'max-content',
  },
  menuList: {
    width: 'max-content',
  },
  selectionSpacer: {
    width: '16px',
    flexShrink: 0,
  },
  // --- Pattern preview ---
  preview: {
    display: 'inline-flex',
    flexDirection: 'column',
    gap: '6px',
    fontSize: '12px',
    lineHeight: 1.4,
    whiteSpace: 'nowrap',
  },
  previewRow: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
  },
  previewMarker: {
    display: 'inline-flex',
    alignItems: 'center',
    lineHeight: 1,
  },
  previewLine: {
    width: PREVIEW_LINE_WIDTH,
    height: '2px',
    backgroundColor: tokens.colorNeutralStroke2,
    borderRadius: tokens.borderRadiusCircular,
    flexShrink: 0,
  },
});

/** Read the listPattern attribute from the root list. */
const getRootListPattern = (editor: Editor, listTypeName: string): string | null => {
  const $from = editor.state.selection.$from as ResolvedPos;
  const root = getRootList($from, listTypeName);
  return (root?.node.attrs.listPattern as string | null) ?? null;
};

/** Whether the current selection is in this list type as the nearest enclosing list. */
const isCurrentListType = (editor: Editor, listTypeName: string): boolean => {
  const $from = editor.state.selection.$from as ResolvedPos;
  return !!getRootList($from, listTypeName);
};

/** Render a pattern preview: each marker on its own indented row with a gray line. */
const PatternPreview = ({
  markers,
  classes,
}: {
  markers: readonly string[];
  classes: ReturnType<typeof useStyles>;
}) => (
  <span className={classes.preview}>
    {markers.map((marker, i) => (
      <span
        key={i}
        className={classes.previewRow}
        style={i > 0 ? { marginLeft: `calc(${PREVIEW_INDENT} * ${i})` } : undefined}
      >
        <span className={classes.previewMarker}>{marker}</span>
        <span className={classes.previewLine} />
      </span>
    ))}
  </span>
);

type TPatternItem = {
  id: string;
  markers: readonly string[];
};

type TProps = {
  menuClasses: { menu: string };
  /** TipTap selector key for isActive check */
  selector: ETiptapSelectorKey;
  /** Main button icon */
  icon: TIcon;
  /** Main button tooltip */
  tooltip: string;
  /** Chevron tooltip when active / inactive */
  chevronTooltip: { active: string; inactive: string };
  /** Called when the main button is clicked */
  onToggle: () => void;
  /** Disable the main toggle button (e.g. inside a heading, where a list can't apply) */
  disabled?: (editor: Editor) => boolean;
  /** Pattern options shown in the dropdown */
  patterns: readonly TPatternItem[];
  /** The default pattern ID (used when attribute is null) */
  defaultPattern: string;
  /** Called when a pattern is selected. Receives null for the default. */
  onSelectPattern: (pattern: string | null) => void;
  /** Extra menu items rendered BEFORE the pattern list (e.g., continue/restart for OL) */
  extraMenuItems?: ReactNode;
};

const ListSplitButton = ({
  menuClasses,
  selector,
  icon,
  tooltip,
  chevronTooltip,
  onToggle,
  disabled,
  patterns,
  defaultPattern,
  onSelectPattern,
  extraMenuItems,
}: TProps) => {
  const classes = useStyles();

  const [open, setOpen] = useState(false);

  const { isActive, currentPattern } = useEditorActive((editor) => {
    const active = isCurrentListType(editor, selector);
    return {
      isActive: active,
      currentPattern: active ? getRootListPattern(editor, selector) : null,
    };
  });

  return (
    <ToolbarSplitButton
      isOpen={open}
      isChevronDisabled={!isActive}
      renderMain={(className) => (
        <ToolbarToggleButton
          selector={(editor) => isCurrentListType(editor, selector)}
          className={className}
          tooltip={tooltip}
          disabled={disabled}
          onClick={onToggle}
          icon={icon}
        />
      )}
      renderChevron={(className) => (
        <Menu open={open} onOpenChange={(_, d) => setOpen(d.open)}>
          <MenuTrigger disableButtonEnhancement>
            <ToolbarMenuButton
              disabled={!isActive}
              tooltip={isActive ? chevronTooltip.active : chevronTooltip.inactive}
              className={className}
              menuIcon={<ChevronDown12Regular />}
            />
          </MenuTrigger>

          <MenuPopover className={mergeClasses(menuClasses.menu, classes.menuPopover)}>
            <MenuList className={classes.menuList}>
              {extraMenuItems}
              {patterns.map(({ id, markers }) => {
                const isSelected = id === (currentPattern ?? defaultPattern);
                return (
                  <ToolbarMenuItem
                    key={id}
                    icon={isSelected ? <Checkmark16Regular /> : <span className={classes.selectionSpacer} />}
                    onClick={() => onSelectPattern(id === defaultPattern ? null : id)}
                  >
                    <PatternPreview markers={markers} classes={classes} />
                  </ToolbarMenuItem>
                );
              })}
            </MenuList>
          </MenuPopover>
        </Menu>
      )}
    />
  );
};

export default ListSplitButton;
